import { CustomCard, DropdownComponent, InsightCard } from 'Components/molecules';
import './styles.scss';
import { useI18n } from 'Hooks/useI18n';
import { Typography } from 'Components/atoms';
import { getAnalyticsFindingMetricsWithDepartments, getAnalyticsUseCases } from 'Apis/library';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import Antenna from 'Assets/gif/Antenna.gif';
import { CartesianDataset } from 'Core-utils/types/types';
import CartesianChart from 'Components/molecules/CartesianChart/CartesianChart';
import {
    AgFindingsGrid,
    DEFAULT_GROUPED_PROGRESS_CONFIG,
    GroupedProgress,
    GroupedProgressConfig,
    mapDepartmentMetricsToGroupedProgress,
    SnapPageCarousel,
} from 'Components/organisms';
import { filterMap, securityFindingsColumnMap } from 'Core-utils/constants/constants';
import { Category, DepartmentMetricsDto, FindingMetricsSeriesDto } from '@ampsec/platform-client';
import { startCase } from 'lodash';
import { buildChartDataset, formatAnalyticsPeriodLabel, validateChartConfig } from './analyticsChartConfigUtils';
import type { AnalyticsChartConfig, AnalyticsChartRowLike, AnalyticsPeriodType } from './analyticsChartConfigUtils';
import { DEFAULT_ANALYTICS_INSIGHT_CARDS_CONFIG } from './analyticsInsightCardsConfig';
import type { AnalyticsInsightCardConfig, AnalyticsInsightCardsConfig } from './analyticsInsightCardsConfig';
import { DEFAULT_ANALYTICS_CHART_CONFIGURATION } from './analyticsChartsConfig';

/** Chart data shape from analytics API (matches FindingMetricsSeriesDto). */
type AnalyticsChartRow = FindingMetricsSeriesDto;

const ALL_USE_CASES_VALUE = 'ALL';
const SELECTED_USE_CASE_PARAM = 'selectedUseCase';
const USER_DEPARTMENT_PARAM = 'user_department';
const CHARTS_PER_PAGE = 2;
const ANALYTICS_PERIOD_TYPE: AnalyticsPeriodType = 'Week';
const ANALYTICS_PERIOD_COUNT = 12;

/** Use case option with backend-provided findingKinds for metrics and grid filter. */
export type AnalyticsUseCaseOption = {
    id: number | string;
    value: string;
    displayValue: string;
    findingKinds?: string;
    group?: 'flowSpec' | 'category';
    disabled?: boolean;
};

function getUseCaseFromParam(param: string | null): string {
    if (param === null || param === undefined || param === '') {
        return ALL_USE_CASES_VALUE;
    }
    const normalized = param.trim();
    if (normalized.toLowerCase() === 'all use cases' || normalized.toUpperCase() === ALL_USE_CASES_VALUE) {
        return ALL_USE_CASES_VALUE;
    }
    return normalized;
}

// Brief delay when switching use case so loading state is visible. Later can be replaced with real "all data loaded" (e.g. grid + charts).
const USE_CASE_SWITCH_LOADING_MS = 200;

// filterMap keys used for categories that have custom finding-kind lists (for grid filter only when API option not available).
const CATEGORY_TO_FILTER_MAP_KEY: Partial<Record<Category, keyof typeof filterMap>> = {
    [Category.VULNERABILITY]: 'Vulnerabilities',
    [Category.TRAINING]: 'Training',
    [Category.AI_SERVICE_RISK]: 'AI Service Risk',
};

const ALL_OPTION: AnalyticsUseCaseOption = {
    id: 0,
    value: ALL_USE_CASES_VALUE,
    displayValue: 'All Use Cases',
};

const FALLBACK_USE_CASE_OPTIONS: AnalyticsUseCaseOption[] = [ALL_OPTION];

function buildUseCaseOptionsFromResponse(
    flowSpecUseCases: AnalyticsUseCaseOption[],
    categoryUseCases: AnalyticsUseCaseOption[],
    getFilteredCategories: (opts: AnalyticsUseCaseOption[]) => AnalyticsUseCaseOption[],
): AnalyticsUseCaseOption[] {
    const filteredCategories = getFilteredCategories(categoryUseCases);
    const flowSpecOptions: AnalyticsUseCaseOption[] = flowSpecUseCases.map((o) => ({
        id: o.id,
        value: o.value,
        displayValue: o.displayValue ?? o.value,
        findingKinds: o.findingKinds,
        group: 'flowSpec',
    }));
    const categoryOptions: AnalyticsUseCaseOption[] = filteredCategories.map((o, idx) => ({
        id: o.id ?? `cat-${idx}`,
        value: o.value,
        displayValue: o.displayValue ?? o.value,
        findingKinds: o.findingKinds,
        group: 'category',
    }));
    return [ALL_OPTION, ...flowSpecOptions, ...categoryOptions];
}

export type PreparedChart = {
    config: AnalyticsChartConfig;
    dataset: CartesianDataset | null;
    error: string | null;
};

export const AnalyticsDashboardPage = () => {
    const { t, loading: i18nLoading } = useI18n();
    const [searchParams, setSearchParams] = useSearchParams();
    const urlUseCase = getUseCaseFromParam(searchParams.get(SELECTED_USE_CASE_PARAM));
    const [selectedUseCase, setSelectedUseCase] = useState<string>(() =>
        typeof window !== 'undefined'
            ? getUseCaseFromParam(new URLSearchParams(window.location.search).get(SELECTED_USE_CASE_PARAM))
            : ALL_USE_CASES_VALUE,
    );
    const [isUseCaseLoading, setIsUseCaseLoading] = useState(false);
    const useCaseLoadingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [analyticsData, setAnalyticsData] = useState<AnalyticsChartRow[]>([]);
    const [departmentMetrics, setDepartmentMetrics] = useState<DepartmentMetricsDto[]>([]);
    const [analyticsLoading, setAnalyticsLoading] = useState(true);
    const [analyticsError, setAnalyticsError] = useState<string | null>(null);
    const [chartConfiguration, setChartConfiguration] = useState<Record<string, AnalyticsChartConfig[]>>({});
    const [insightCardsConfig, setInsightCardsConfig] = useState<AnalyticsInsightCardsConfig>([]);
    const [chartConfigReady, setChartConfigReady] = useState(false);
    const [useCaseOptions, setUseCaseOptions] = useState<AnalyticsUseCaseOption[]>([]);
    const [groupedProgressConfig, setGroupedProgressConfig] = useState<GroupedProgressConfig>(
        DEFAULT_GROUPED_PROGRESS_CONFIG,
    );
    useEffect(() => {
        if (i18nLoading) {
            return;
        }
        let cancelled = false;

        function getFilteredCategories(opts: AnalyticsUseCaseOption[]): AnalyticsUseCaseOption[] {
            try {
                const useCasesRaw = t('ANALYTICS_VIEW_USE_CASES_FILTERED_CATEGORIES');
                const filteredUseCases = JSON.parse(useCasesRaw) as Category[];
                return opts.filter((opt) => !filteredUseCases.includes(opt.value as Category));
            } catch {
                return opts;
            }
        }

        getAnalyticsUseCases({ includeFlowSpecUseCases: true })
            .then(({ flowSpecUseCases, categoryUseCases }) => {
                if (cancelled) {
                    return;
                }
                setUseCaseOptions(
                    buildUseCaseOptionsFromResponse(flowSpecUseCases, categoryUseCases, getFilteredCategories),
                );
            })
            .catch(() => {
                if (cancelled) {
                    return;
                }
                setUseCaseOptions(FALLBACK_USE_CASE_OPTIONS);
            });

        return () => {
            cancelled = true;
        };
    }, [t, i18nLoading]);

    useEffect(() => {
        if (i18nLoading) {
            return;
        }
        try {
            const configsRaw = t('ANALYTICS_VIEW_CHART_CONFIGURATION');
            const configs = JSON.parse(configsRaw);
            setChartConfiguration(configs as Record<string, AnalyticsChartConfig[]>);
        } catch {
            // Keep existing chart configuration on parse error
            setChartConfiguration(DEFAULT_ANALYTICS_CHART_CONFIGURATION);
        }
        try {
            const cardsRaw = t('ANALYTICS_VIEW_INSIGHT_CARDS_CONFIGURATION');
            const cards = JSON.parse(cardsRaw) as AnalyticsInsightCardsConfig;
            if (Array.isArray(cards) && cards.length > 0) {
                setInsightCardsConfig(cards.slice(0, 4));
            }
        } catch {
            // Use default config
            setInsightCardsConfig(DEFAULT_ANALYTICS_INSIGHT_CARDS_CONFIG);
        }

        try {
            const groupedProgressConfigRaw = t('ANALYTICS_VIEW_GROUPED_PROGRESS_CONFIGURATION');
            const parsedProgressConfig = JSON.parse(groupedProgressConfigRaw) as GroupedProgressConfig;
            setGroupedProgressConfig(parsedProgressConfig);
        } catch {
            // Use default config
            setGroupedProgressConfig(DEFAULT_GROUPED_PROGRESS_CONFIG);
        } finally {
            setChartConfigReady(true);
        }
    }, [t, i18nLoading]);

    const selectedOption = useMemo(
        () => useCaseOptions.find((o) => o.value === selectedUseCase && !o.disabled),
        [useCaseOptions, selectedUseCase],
    );

    const findingKindsForMetrics = useMemo(() => {
        if (selectedUseCase === ALL_USE_CASES_VALUE) {
            return undefined;
        }
        return selectedOption?.findingKinds;
    }, [selectedUseCase, selectedOption]);

    const findingFilter = useMemo(() => {
        if (selectedUseCase === ALL_USE_CASES_VALUE || !selectedOption) {
            return {};
        }
        if (selectedOption.group === 'flowSpec' && selectedOption.findingKinds) {
            return {
                findingKind: selectedOption.findingKinds
                    .split(',')
                    .map((k) => k.trim())
                    .filter(Boolean),
            };
        }
        if (selectedOption.group === 'category') {
            const filterMapKey = CATEGORY_TO_FILTER_MAP_KEY[selectedOption.value as Category];
            if (filterMapKey) {
                const mapEntry = filterMap[filterMapKey];
                const category = mapEntry?.[securityFindingsColumnMap.findingCategory];
                const kind = mapEntry?.[securityFindingsColumnMap.findingKind];
                return {
                    ...(category ? { findingCategory: category } : {}),
                    ...(kind ? { findingKind: kind } : {}),
                };
            }
            return { findingCategory: [selectedOption.value] };
        }
        return {};
    }, [selectedUseCase, selectedOption]);

    const useCaseDisplayName = useMemo(() => {
        if (selectedUseCase === ALL_USE_CASES_VALUE) {
            return 'All Use Cases';
        }
        return selectedOption?.displayValue ?? startCase(selectedUseCase);
    }, [selectedUseCase, selectedOption]);

    useEffect(() => {
        if (urlUseCase !== selectedUseCase) {
            setSelectedUseCase(urlUseCase);
        }
    }, [urlUseCase]);

    useEffect(() => {
        const params = {
            findingKinds: findingKindsForMetrics,
            periodType: ANALYTICS_PERIOD_TYPE,
            periodCount: ANALYTICS_PERIOD_COUNT,
        };
        let cancelled = false;
        setAnalyticsLoading(true);
        setAnalyticsError(null);
        getAnalyticsFindingMetricsWithDepartments(params)
            .then((response) => {
                if (!cancelled) {
                    setAnalyticsData(Array.isArray(response.data) ? response.data : []);
                    setDepartmentMetrics(response.departments ?? []);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setAnalyticsError(err?.message ?? 'Failed to load analytics');
                    setAnalyticsData([]);
                    setDepartmentMetrics([]);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setAnalyticsLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [findingKindsForMetrics]);

    // When use case switch puts us in loading state, clear it after a short delay so we render the grid.
    // Later this can wait for real "all data loaded" (e.g. grid + chart fetches).
    useEffect(() => {
        if (i18nLoading) {
            return;
        }
        if (!isUseCaseLoading) {
            return;
        }
        if (useCaseLoadingTimeoutRef.current) {
            clearTimeout(useCaseLoadingTimeoutRef.current);
        }
        useCaseLoadingTimeoutRef.current = setTimeout(() => {
            useCaseLoadingTimeoutRef.current = null;
            setIsUseCaseLoading(false);
        }, USE_CASE_SWITCH_LOADING_MS);
        return () => {
            if (useCaseLoadingTimeoutRef.current) {
                clearTimeout(useCaseLoadingTimeoutRef.current);
            }
        };
    }, [i18nLoading, isUseCaseLoading]);

    const onUseCaseChange = useCallback(
        (eventKey: string | null, _e: unknown) => {
            if (eventKey === null || eventKey === undefined) {
                return;
            }
            const option = useCaseOptions.find((o) => String(o.id) === eventKey || o.value === eventKey);
            if (option && !option.disabled) {
                setIsUseCaseLoading(true);
                setSelectedUseCase(option.value);
                const next = new URLSearchParams({ [SELECTED_USE_CASE_PARAM]: option.value });
                setSearchParams(next);
            }
        },
        [setSearchParams, useCaseOptions],
    );

    const selectedGroupId = searchParams.get(USER_DEPARTMENT_PARAM) ?? null;

    const onGroupClick = useCallback(
        (groupId: string) => {
            setSearchParams((prev) => {
                // if the groupId is the same as the current selectedGroupId, remove the param
                const next = new URLSearchParams(prev);
                if (groupId === selectedGroupId) {
                    next.delete(USER_DEPARTMENT_PARAM);
                    return next;
                }
                next.set(USER_DEPARTMENT_PARAM, groupId);
                return next;
            });
        },
        [setSearchParams],
    );

    const data = analyticsData;

    const chartsForUseCase = useMemo(() => {
        const key =
            selectedUseCase === ALL_USE_CASES_VALUE
                ? 'default'
                : selectedOption?.group === 'flowSpec'
                  ? 'FLOW_SPEC'
                  : selectedUseCase;
        const configs =
            chartConfiguration[key] ??
            (key === 'FLOW_SPEC' ? DEFAULT_ANALYTICS_CHART_CONFIGURATION['FLOW_SPEC'] : undefined);
        if (!Array.isArray(configs) || configs.length === 0) {
            return [];
        }
        return configs;
    }, [selectedUseCase, selectedOption?.group, chartConfiguration]);

    const preparedCharts = useMemo((): PreparedChart[] => {
        return chartsForUseCase.map((config) => {
            const validationError = validateChartConfig(config);
            if (validationError) {
                return { config, dataset: null, error: validationError };
            }
            if (!data.length) {
                return { config, dataset: null, error: null };
            }
            const built = buildChartDataset(config, data as AnalyticsChartRowLike[], ANALYTICS_PERIOD_TYPE);
            return {
                config,
                dataset: { labels: built.labels, data: built.data },
                error: null,
            };
        });
    }, [chartsForUseCase, data]);

    const chartPageCount = Math.ceil(preparedCharts.length / CHARTS_PER_PAGE) || 0;
    const initialChartPage = 0;

    const onChartPageChange = useCallback(() => {
        // TODO: track chart page in URL
    }, [setSearchParams]);

    const isPageLoading = i18nLoading || !chartConfigReady || analyticsLoading;
    const analyticsPeriodLabel = useMemo(() => {
        const firstPeriod = analyticsData?.[0]?.period;
        const lastPeriod = analyticsData?.[analyticsData.length - 1]?.period;
        if (!firstPeriod || !lastPeriod) {
            return '';
        }
        const firstLabel = formatAnalyticsPeriodLabel(firstPeriod, ANALYTICS_PERIOD_TYPE);
        const lastLabel = formatAnalyticsPeriodLabel(lastPeriod, ANALYTICS_PERIOD_TYPE);
        return `${firstLabel} - ${lastLabel}`;
    }, [analyticsData]);

    if (isPageLoading) {
        return (
            <div id="analytics-dashboard-page" className="analytics-dashboard-page analytics-dashboard-page--loading">
                <div className="analytics-dashboard-page__loading">
                    <img src={Antenna} alt="loading" />
                </div>
            </div>
        );
    }

    return (
        <div id="analytics-dashboard-page" className="analytics-dashboard-page d-flex flex-column gap-3 p-3">
            <div className="analytics-insights-card analytics-dashboard-header">
                <div className="justify-content-start">
                    <Typography variant="h4" color="text-high-emphasis">
                        Finding Insights Dashboard
                    </Typography>
                    <Typography variant="subtitle3" color="text-medium-emphasis">{`${t(
                        'ANALYTICS_VIEW_AGGREGATED_FINDINGS_FOR_PERIOD',
                    )} ${analyticsPeriodLabel}`}</Typography>
                </div>
                <div className="justify-content-end">
                    <div className="dashboard__dropdown-container">
                        <DropdownComponent
                            value={useCaseDisplayName}
                            dropDownItems={useCaseOptions.map((o) => ({
                                id: o.id,
                                value: o.value,
                                displayValue: o.displayValue,
                                disabled: o.disabled,
                            }))}
                            variant="default"
                            onChange={onUseCaseChange}
                        />
                    </div>
                </div>
            </div>
            <div className="d-flex flex-row gap-3 w-100">
                {insightCardsConfig.slice(0, 4).map((card: AnalyticsInsightCardConfig, index: number) => {
                    const latestRow = analyticsData[analyticsData.length - 1];
                    const value =
                        latestRow !== undefined && latestRow !== null && card.metric in latestRow
                            ? (latestRow as unknown as Record<string, unknown>)[card.metric]
                            : undefined;
                    return (
                        <InsightCard
                            key={`insight-card-${index}-${card.metric}`}
                            className="analytics-dashboard-page__insight-card"
                        >
                            <InsightCard.Title>
                                <Typography variant="subtitle1" color="text-high-emphasis">
                                    {card.title}
                                </Typography>
                            </InsightCard.Title>
                            <InsightCard.Subtitle>
                                <Typography variant="subtitle3" color="text-medium-emphasis">
                                    {card.subtitle}
                                </Typography>
                            </InsightCard.Subtitle>
                            <InsightCard.Value>
                                <InsightCard.ValueNumber className={card.valueClassName}>
                                    {value !== undefined && value !== null ? String(value) : ''}
                                </InsightCard.ValueNumber>
                                {card.unit && value !== undefined && value !== null && (
                                    <InsightCard.ValueUnit>{card.unit}</InsightCard.ValueUnit>
                                )}
                            </InsightCard.Value>
                        </InsightCard>
                    );
                })}
            </div>
            <div>
                <CustomCard cardType="custom" height="auto !important">
                    {analyticsError ? (
                        <div className="d-flex flex-column align-items-center justify-content-center py-5 gap-2">
                            <Typography variant="body10" color="text-medium-emphasis">
                                {analyticsError}
                            </Typography>
                        </div>
                    ) : preparedCharts.length === 0 ? null : (
                        <SnapPageCarousel
                            pageCount={chartPageCount}
                            initialPage={initialChartPage}
                            onPageChange={onChartPageChange}
                            renderPage={(pageIndex) => {
                                const start = pageIndex * CHARTS_PER_PAGE;
                                const pageCharts = preparedCharts.slice(start, start + CHARTS_PER_PAGE);
                                return (
                                    <div className="analytics-dashboard-page__charts-page">
                                        {pageCharts.map((prepared) => (
                                            <div key={prepared.config.id} className="chart-wrapper">
                                                <div id={prepared.config.id} className="chartjs-container">
                                                    {prepared.error ? (
                                                        <div className="d-flex flex-column align-items-center justify-content-center py-5 gap-2">
                                                            <Typography variant="body10" color="text-medium-emphasis">
                                                                Chart error: {prepared.error}
                                                            </Typography>
                                                        </div>
                                                    ) : (
                                                        <CartesianChart
                                                            labels={prepared.dataset?.labels}
                                                            title={prepared.config.title}
                                                            subtitle={prepared.config.subtitle}
                                                            dataset={prepared.dataset ?? undefined}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                );
                            }}
                        />
                    )}
                </CustomCard>
            </div>
            {departmentMetrics.length > 0 && (
                <div className="w-100">
                    <CustomCard cardType="custom" height="auto !important">
                        <GroupedProgress
                            groups={mapDepartmentMetricsToGroupedProgress(departmentMetrics)}
                            config={groupedProgressConfig}
                            onGroupClick={onGroupClick}
                            selectedGroupId={selectedGroupId}
                        />
                    </CustomCard>
                </div>
            )}
            <div className="w-100">
                <CustomCard cardType="custom">
                    <div className="ag-findings-grid__embed-wrapper w-100 analytics-dashboard__findings-section">
                        <div className="justify-content-start">
                            <Typography variant="h4" color="text-high-emphasis">
                                {t('ANALYTICS_VIEW_FINDINGS_TABLE_TITLE')}
                            </Typography>
                            <Typography variant="subtitle3" color="text-medium-emphasis">
                                {t('ANALYTICS_VIEW_FINDINGS_TABLE_SUBTITLE')}
                            </Typography>
                        </div>
                        {isUseCaseLoading ? (
                            <div className="analytics-dashboard__findings-loading d-flex flex-column align-items-center justify-content-center py-5 gap-2">
                                <Spinner animation="border" variant="primary" />
                                <Typography variant="body10" color="text-medium-emphasis">
                                    Loading findings…
                                </Typography>
                            </div>
                        ) : (
                            <AgFindingsGrid
                                key={selectedUseCase}
                                findingCategory={findingFilter.findingCategory}
                                findingKind={findingFilter.findingKind}
                                enableUpdatingURLWithFiltersAndSorting={true}
                                showSubHeader={false}
                                disableFilters={false}
                                showHeaderActions={false}
                                fullWidth
                                preserveUrlParamKeys={[SELECTED_USE_CASE_PARAM, USER_DEPARTMENT_PARAM]}
                                showOnlyColumns={[
                                    'user_name',
                                    'finding_title',
                                    'description',
                                    'department',
                                    'discoveredAt',
                                    'user_department',
                                    'provider_name',
                                    'triggerable_agents_count',
                                ]}
                            />
                        )}
                    </div>
                </CustomCard>
            </div>
        </div>
    );
};

export default AnalyticsDashboardPage;
